const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const authHeaders = () => {
  const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
  return {
    Authorization: `Bearer ${token}`,
    "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY,
  };
};

// Drops empty/undefined/null filter values. Array values (the multi-select
// filters) are sent as repeated params (plant=A&plant=B) so values that
// contain commas survive; endpoints that still expect one comma-separated
// string pass { joinArrays: true }.
export const buildMetricsQuery = (params = {}, { joinArrays = false } = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      const values = value.filter((v) => v !== undefined && v !== null && v !== "");
      if (values.length === 0) return;
      if (joinArrays) query.set(key, values.join(","));
      else values.forEach((v) => query.append(key, v));
      return;
    }
    query.set(key, value);
  });
  return query;
};

const getMetric = async (path, filters, { joinArrays = false } = {}) => {
  const query = buildMetricsQuery(filters, { joinArrays });
  const res = await fetch(`${BASE_URL}${path}?${query}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    // Backend validation failures (e.g. a bad baseline) return {"error": "..."}.
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `${path} failed: ${res.status}`);
  }
  return res.json();
};

// Same endpoint Home.js uses to populate its select filters.
export const getFilterValues = async () => {
  const res = await fetch(`${BASE_URL}/filter-values`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`/filter-values failed: ${res.status}`);
  return res.json();
};

// Distinct plant / material / approver / po_creator values: { plant: [...], ... }.
export const getFilterOptions = async () => {
  const res = await fetch(`${BASE_URL}/metrics/filter-options`, { headers: authHeaders() });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `/metrics/filter-options failed: ${res.status}`);
  }
  return res.json();
};

export const getSyncTablePreview = (table) => getMetric("/sync-table-preview", { table });

export const getPendingApprovalAging = (filters) => getMetric("/pending-approval-aging", filters);
export const getPendingPoAging = (filters) => getMetric("/pending-po-aging", filters);
export const getApprovalTimeliness = (filters) => getMetric("/approval-timeliness-performance", filters);
export const getPoTimeliness = (filters) => getMetric("/po-timeliness-performance", filters);
export const getInvoiceTimeliness = (filters) => getMetric("/invoice-timeliness-performance", filters);
export const getCostCountAnalytics = (filters) => getMetric("/cost-count-analytics", filters);
export const getInvalidCreditNoteOverview = (filters) => getMetric("/invalid-credit-note-overview", filters);
export const getE2ETimelinessPerformance = (filters) => getMetric("/e2e-timeliness-performance", filters);
// baseline is the one array param the backend still wants comma-separated (2024,2025).
export const getMaterialSpendCostSpike = (filters = {}) =>
  getMetric("/material-spend-cost-spike", {
    ...filters,
    baseline: Array.isArray(filters.baseline) ? filters.baseline.join(",") : filters.baseline,
  });
export const getAwaitingTimeliness = (filters) => getMetric("/awaiting-timeliness-performance", filters);
export const getRejectionDashboard = (filters) => getMetric("/rejection-dashboard", filters);
// These two still take one comma-separated string per filter (backend not yet
// migrated to repeated params), so values containing commas will be split.
export const getChargeSla = (filters) => getMetric("/charge-sla", filters, { joinArrays: true });
export const getDndChargeSla = (filters) => getMetric("/dnd-charge-sla", filters, { joinArrays: true });

export const getCxTimelinessOverview = async (filters) => {
  const requests = [getChargeSla, getDndChargeSla].flatMap((fetcher) =>
    ["Origin", "Destination"].map((charge_type) =>
      fetcher({ ...filters, page: 1, page_size: 1, charge_type })
    )
  );
  const [standardOrigin, standardDestination, dndOrigin, dndDestination] = await Promise.all(requests);
  const summarize = (origin, destination) => {
    const counts = [origin, destination].map((data) => data.sla_summary || {});
    const within = counts.reduce((total, summary) => total + Number(summary.within_sla || 0), 0);
    const breached = counts.reduce((total, summary) => total + Number(summary.breached || 0), 0);
    const known = within + breached;
    return { within, breached, within_sla_pct: known ? (within / known) * 100 : 0 };
  };
  const standard = summarize(standardOrigin, standardDestination);
  const dnd = summarize(dndOrigin, dndDestination);
  const overallWithin = standard.within + dnd.within;
  const overallBreached = standard.breached + dnd.breached;
  const overallKnown = overallWithin + overallBreached;

  return {
    overall: {
      within_sla_pct: overallKnown ? (overallWithin / overallKnown) * 100 : 0,
    },
    dnd,
  };
};
